import React, { useEffect, useState } from 'react'
import cx from 'classnames'
import { object, string, bool, func, number, array } from 'prop-types'
import Truncate from 'react-truncate'
import SmartLink from '@/component/Primitive/SmartLink'

import Type from '../../Type'
import Hide from '../../Hide'
import Inline from '../../Inline'

import styles from '../Card.module.scss'
import { cardContentType } from '..'

const CardContent = ({
  description,
  title,
  subtitle,
  shouldShowSubtitle,
  headerComponents,
  footerComponents,
  contentBorder,
  cappedHeight,
  typeConfig,
  truncateTitleAt,
  truncateDescriptionAt,
  clickableFooter,
  hideDescriptionOnMobile,
  classNames,
  link,
  to,
  eventBaseSlug,
  hasShade
}) => {
  const Header = headerComponents
  const Footer = footerComponents

  const [fontsReady, setFontsReady] = useState(false)

  useEffect(() => {
    if (fontsReady) return
    document.fonts.ready.then(() => setFontsReady(true))
  })

  return (
    <div
      className={cx(
        styles.CardContent,
        contentBorder?.includes('bottom') && styles['border-bottom'],
        contentBorder?.includes('top') && styles['border-top'],
        cappedHeight && styles.cappedHeight,
        classNames?.root
      )}
    >
      <div className={cx(styles.CardInner, classNames?.inner)}>
        {headerComponents && (
          <div className={styles.CardHeader}>
            <Inline middle gap="small">
              <Header />
            </Inline>
          </div>
        )}
        <div className={cx(styles.CardTitle, classNames?.title)}>
          <Type
            as="h3"
            weight={typeConfig?.title?.weight || 'bold'}
            size={typeConfig?.title?.size || 'heading7'}
            themed={typeConfig?.title?.themed}
          >
            <SmartLink
              to={to || (eventBaseSlug ? '/eventArticle' : '/article')}
              as={link}
              className={cx(styles.CardLink, classNames?.link)}
            >
              {truncateTitleAt && fontsReady ? (
                <Truncate lines={truncateTitleAt}>{title}</Truncate>
              ) : (
                title
              )}
            </SmartLink>
          </Type>
        </div>
        {shouldShowSubtitle && subtitle && (
          <div className={cx(styles.CardSubTitle)}>
            <Type
              as="h3"
              weight={typeConfig?.subtitle?.weight || 'medium'}
              size={typeConfig?.subtitle?.size || ['body2', 'body3']}
              color={typeConfig?.subtitle?.color}
              themed={typeConfig?.subtitle?.themed}
            >
              <SmartLink
                to={to || (eventBaseSlug ? '/eventArticle' : '/article')}
                as={link}
                className={cx(styles.CardLink, classNames?.link)}
              >
                {subtitle}
              </SmartLink>
            </Type>
          </div>
        )}
        {description && (
          <Hide below={hideDescriptionOnMobile && 'desktop'}>
            <div
              className={cx(styles.CardDescription, classNames?.description)}
            >
              <Type
                as="p"
                size={typeConfig?.description?.size || ['body2', 'body3']}
              >
                <Truncate lines={truncateDescriptionAt}>{description}</Truncate>
              </Type>
            </div>
          </Hide>
        )}
      </div>
      {footerComponents && (
        <div className={cx(styles.CardFooter, clickableFooter && styles.onTop)}>
          <Footer />
        </div>
      )}
      {hasShade && <div className={cx(styles.CardContentShade)} />}
    </div>
  )
}

CardContent.defaultProps = {
  truncateTitleAt: null,
  truncateDescriptionAt: 4,
  hasShade: false
}

CardContent.propTypes = {
  description: string,
  title: string.isRequired,
  subtitle: string,
  shouldShowSubtitle: bool,
  headerComponents: func,
  footerComponents: func,
  contentBorder: array,
  clickableFooter: bool,
  cappedHeight: bool,
  typeConfig: object,
  truncateTitleAt: number,
  truncateDescriptionAt: number,
  hideDescriptionOnMobile: bool,
  classNames: cardContentType,
  link: string.isRequired,
  to: string,
  eventBaseSlug: string,
  hasShade: bool
}

export default CardContent
